import {useEffect, useState, lazy, Suspense} from 'react'
import {Navigate, useRoutes} from 'react-router-dom'

import {useAuth} from './components/SessionAuth'
import StartingLoader from './components/StartingLoader'
import AppBootLoader from './components/AppBootLoader'
import user_api from 'utils/api/users'
import AuthRouteFallback from './components/authentication/AuthRouteFallback'
import TrialAccessGate from './components/trial/TrialAccessGate'
import {
  EmployeeRegisterRedirect,
  StoreCreateRedirect,
  GroupCreateRedirect,
} from './components/authentication/registration/LegacyRedirects'

// Layouts – lazy so shell doesn’t block initial load
const DashboardLayout = lazy(() => import('./layouts/dashboard'))
const LogoOnlyLayout = lazy(() => import('./layouts/LogoOnlyLayout'))
const SupportConsolePage = lazy(() => import('./pages/SupportConsole'))

// Auth pages
const LoginLayout = lazy(() => import('./pages/LoginLayout'))
const Login = lazy(() => import('./pages/Login'))
const StoreLogin = lazy(() => import('./pages/StoreLogin'))
const RegistrationWizard = lazy(() => import('./components/authentication/registration/RegistrationWizard'))

// Shared/standalone pages
const StoreOnboard = lazy(() => import('./pages/StoreOnboard'))
const TrackTime = lazy(() => import('./pages/TrackTime'))
const DemoLogin = lazy(() => import('./pages/LoginDemo'))
const Onboard = lazy(() => import('./pages/Onboard'))
const NotFound = lazy(() => import('./pages/Page404'))
const StoreMissing = lazy(() => import('./pages/StoreMissing'))

// Employee dashboard pages
const EmployeeDashboard = lazy(() => import('./pages/EmployeeDashboard'))
const EmployeeProfile = lazy(() => import('./pages/EmployeeProfile'))
const EmployeeRequiredDocuments = lazy(() => import('./pages/EmployeeRequiredDocuments'))
const MyCompany = lazy(() => import('./pages/employee-company'))
const EmployeeMyHistory = lazy(() => import('./pages/EmployeeMyHistory'))
const EmployeeRequest = lazy(() => import('./pages/EmployeeRequest'))
const MyRequestsPage = lazy(() => import('./pages/MyRequests'))
const TrackMyRequestPage = lazy(() => import('./pages/TrackMyRequest'))
const Leaderboards = lazy(() => import('./pages/Leaderboards'))
const BulletinBoard = lazy(() => import('./pages/BulletinBoard'))

// Store dashboard pages
const StoreDashboard = lazy(() => import('./pages/StoreDashboard'))
const StoreReports = lazy(() => import('./pages/StoreReports')) //done
const User = lazy(() => import('./pages/User'))// done
const TimeAdjustment = lazy(() => import('./pages/TimeAdjustment'))//done
const Admin = lazy(() => import('./pages/Admin'))
const Subscription = lazy(() => import('./pages/Subscription'))
const StoreQR = lazy(() => import('./pages/StoreQR'))//done
const StoreBranches = lazy(() => import('./pages/StoreBranches'))
const Store = lazy(() => import('./pages/Store'))
const StoreDetails = lazy(() => import('./pages/StoreDetails'))
const Products = lazy(() => import('./pages/Products'))
const Blog = lazy(() => import('./pages/Blog'))
const StoreProfile = lazy(() => import('./pages/StoreProfile'))
const Schedule = lazy(() => import('./pages/Schedule'))//done
const Announcements = lazy(() => import('./pages/Announcements'))//done
const BreaklistInfo = lazy(() => import('./pages/BreaklistInfo'))//done
const Breaklist = lazy(() => import('./pages/Breaklist'))//done
const ManageBreaklist = lazy(() => import('./pages/ManageBreaklist'))//done
const StoreApproval = lazy(() => import('./pages/StoreApproval'))
const RequiredDocuments = lazy(() => import('./pages/StoreRequiredDocuments'))//done
const AdminCompany = lazy(() => import('./pages/AdminCompany'))//done
const Feedbacks = lazy(() => import('./pages/Feedback'))
const AdminActionLogs = lazy(() => import('./pages/AdminActionLogs'))
const QrScanTimeKeep = lazy(() => import('./pages/QrScanTimeKeep'))
const Holidays = lazy(() => import('./pages/Holiday'))
const ArchivedGate = lazy(() => import('./pages/ArchivedGate')) //done
const BroadcastPage = lazy(() => import('./pages/Broadcast'))//done
const VersionControl = lazy(() => import('./pages/VersionControl'))//for admin
const AdminTrainingAndDevelopment = lazy(() => import('./pages/AdminTrainingAndDevelopment'))//done
const AdminDiceGame = lazy(() => import('./pages/AdminDiceGame'))
const AdminEasterGame = lazy(() => import('./pages/AdminEasterGame'))
const DailyQuestions = lazy(() => import('./pages/DailyQuestions'))
const Surveys = lazy(() => import('./pages/Surveys'))
const StoreRequestsProcessPage = lazy(() => import('./pages/StoreRequestsProcess'))
const StoreGrouping = lazy(() => import('./pages/StoreGrouping'))

// Marketing (commercial-stk) — lazy-loaded shell and pages
const MarketingLayout = lazy(() => import('./marketing/MarketingLayout'))
const MarketingIndexGate = lazy(() => import('./marketing/MarketingIndexGate'))
const MFeatures = lazy(() => import('marketing-site/app/pages/Features'))
const MFeatureDetail = lazy(() => import('marketing-site/app/pages/FeatureDetail'))
const MHowItWorks = lazy(() => import('marketing-site/app/pages/HowItWorks'))
const MPricing = lazy(() => import('marketing-site/app/pages/Pricing'))
const MAbout = lazy(() => import('marketing-site/app/pages/About'))
const MIntegrations = lazy(() => import('marketing-site/app/pages/Integrations'))
const MUpdates = lazy(() => import('marketing-site/app/pages/Updates'))
const MCareers = lazy(() => import('marketing-site/app/pages/Careers'))
const MBlog = lazy(() => import('marketing-site/app/pages/Blog'))
const MContact = lazy(() => import('marketing-site/app/pages/Contact'))
const MHelpCenter = lazy(() => import('marketing-site/app/pages/HelpCenter'))
const MDocs = lazy(() => import('marketing-site/app/pages/Docs'))
const MApiReference = lazy(() => import('marketing-site/app/pages/ApiReference'))
const MStatus = lazy(() => import('marketing-site/app/pages/Status'))
const withSuspense = (Component) => (
  <Suspense fallback={<StartingLoader />}>
    <Component />
  </Suspense>
)

const withAuthSuspense = (Component) => (
  <Suspense fallback={<AuthRouteFallback />}>
    <Component />
  </Suspense>
)
const ROLE_UNVERIFIED_MESSAGE = "Verifying your role..."

const AppRoute = () => {
  const {isAuthenticated, userData, roleVerified, isLoading} = useAuth()
  const isLoginTransition =
    typeof window !== 'undefined' &&
    sessionStorage.getItem('auth_logging_in') === '1' &&
    !isAuthenticated

  const getUserRole = () => {
    if (!userData || typeof userData !== 'object') return null
    if (typeof userData.role === 'undefined' || userData.role === null) return null
    return userData.role
  }

  const getDashboardPath = () => {
    const userRole = getUserRole()
    if (userRole === null) return null
    return userRole === 0 ? '/dashboard/app' : '/stores/app'
  }

  const userRole = getUserRole()
  const isAuthReady = isAuthenticated && roleVerified && userRole !== null
  const waitingForRole =
    isLoginTransition || isLoading || (isAuthenticated && (!roleVerified || userRole === null))
  const [enabledFeatureSet, setEnabledFeatureSet] = useState(null)

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    // Exit "Logging In..." once session check settles.
    if (!isLoading) {
      try {
        sessionStorage.removeItem('auth_logging_in')
      } catch (_error) {}
      return undefined
    }

    // Failsafe: never let the transition flag block UI indefinitely.
    if (isLoginTransition) {
      const timeoutId = setTimeout(() => {
        try {
          sessionStorage.removeItem('auth_logging_in')
        } catch (_error) {}
      }, 15000)
      return () => clearTimeout(timeoutId)
    }

    return undefined
  }, [isLoading, isLoginTransition])

  useEffect(() => {
    let isCancelled = false
    const loadStoreFeatures = async () => {
      if (!isAuthenticated || waitingForRole || userRole == null || userRole === 0 || !userData?.company) {
        setEnabledFeatureSet(null)
        return
      }
      try {
        const featuresRes = await user_api.get_features({ store: userData.company })
        if (isCancelled) return
        const nextSet = new Set()
        const features = Array.isArray(featuresRes?.data) ? featuresRes.data : []
        features.forEach((feature) => {
          const featureItems = Array.isArray(feature?.features) ? feature.features : []
          featureItems.forEach((f) => {
            const name = typeof f?.feature === 'string' ? f.feature.trim() : ''
            if (name) nextSet.add(name)
          })
        })
        setEnabledFeatureSet(nextSet)
      } catch (_error) {
        if (!isCancelled) setEnabledFeatureSet(new Set())
      }
    }

    loadStoreFeatures()
    return () => {
      isCancelled = true
    }
  }, [isAuthenticated, waitingForRole, userRole, userData?.company])

  const makeFeatureGuardedRoute = ({ featureNames, minRole = 1, Component, loadingLabel = 'Checking feature access...' }) => {
    if (waitingForRole || userRole == null) {
      return <AppBootLoader message={ROLE_UNVERIFIED_MESSAGE} ariaLabel="Verifying user role" />
    }
    if (userRole < minRole) return <Navigate to="/stores/app" replace />
    if (enabledFeatureSet === null) {
      return <AppBootLoader message={loadingLabel} ariaLabel="Checking feature access" />
    }
    const names = Array.isArray(featureNames) ? featureNames : [featureNames]
    const hasAccess = names.some((name) => enabledFeatureSet.has(name))
    return hasAccess ? withSuspense(Component) : <Navigate to="/stores/app" replace />
  }

  return useRoutes([
    {

      //all employee related routes has tour element, to be added later
      path: 'dashboard',
      element: !isAuthenticated ? (
        <Navigate to="/" replace />
      ) : waitingForRole ? (
        <AppBootLoader message={ROLE_UNVERIFIED_MESSAGE} ariaLabel="Verifying user role" />
      ) : isAuthenticated && userRole !== 0 ? (
        <Navigate to="/stores/app" replace />
      ) : (
        withSuspense(DashboardLayout)
      ),
      children: [
        {path: '', element: <Navigate to="app" replace />},
        {path: 'app', element: withSuspense(EmployeeDashboard)},
        {path: 'employee/app', element: withSuspense(EmployeeDashboard)},
        {path: 'app/profile', element: withSuspense(EmployeeProfile)}, // mounted the tour lement
        {path: 'app/required-documents', element: withSuspense(EmployeeRequiredDocuments)}, //mounted tour
        {path: 'app/my-company', element: withSuspense(MyCompany)},//done
        {path: 'app/my-history', element: withSuspense(EmployeeMyHistory)},//done
        {path: 'app/my-requests', element: withSuspense(MyRequestsPage)},//done
        {path: 'app/my-requests/:requestId', element: withSuspense(TrackMyRequestPage)},
        {path: 'app/leaderboards', element: withSuspense(Leaderboards)},
        {path: 'app/bulletin-board', element: withSuspense(BulletinBoard)},
      ],
    },
    {
      path: 'stores',
      element: !isAuthenticated ? (
        <Navigate to="/" replace />
      ) : waitingForRole ? (
        <AppBootLoader message={ROLE_UNVERIFIED_MESSAGE} ariaLabel="Verifying user role" />
      ) : isAuthenticated && userRole === 0 ? (
        <Navigate to="/dashboard/app" replace />
      ) : (
        <TrialAccessGate>{withSuspense(DashboardLayout)}</TrialAccessGate>
      ),
      children: [
        {path: '', element: <Navigate to="app" replace />},
        {path: 'app', element: withSuspense(StoreDashboard)},
        {path: 'user', element: withSuspense(User)}, //onboarding neded ,done
        {path: 'archive', element: <Navigate to="/stores/user?tab=inactive" replace />},
        {path: 'adjustment', element: withSuspense(TimeAdjustment)},//done
        {path: 'admin', element: withSuspense(Admin)},
        {
          path: 'features',
          element: [3, 4].includes(Number(userRole))
            ? withSuspense(Subscription)
            : <Navigate to="/stores/app" replace />,
        },
        {path: 'qr', element: withSuspense(StoreQR)}, //done
        {path: 'reports', element: withSuspense(StoreReports)},
        {path: 'branches', element: withSuspense(StoreBranches)},
        {path: 'store', element: withSuspense(Store)},
        {path: 'store/view/:id', element: withSuspense(StoreDetails)},
        {path: 'products', element: withSuspense(Products)},
        {path: 'blog', element: withSuspense(Blog)},
        {path: 'profile', element: withSuspense(StoreProfile)},
        {path: 'profile/stores', element: withSuspense(StoreProfile)},
        {path: 'schedule', element: withSuspense(Schedule)},
        {path: 'announcements', element: withSuspense(Announcements)},
        {path: 'broadcast', element: withSuspense(BroadcastPage)},
        {path: 'breaklist-info', element: withSuspense(BreaklistInfo)},//done
        {path: 'breaklist', element: withSuspense(Breaklist)},//done
        {path: 'manage-breaklist', element: withSuspense(ManageBreaklist)},//done
        {path: 'approval', element: withSuspense(StoreApproval)},
        {
          path: 'required-documents',
          element: makeFeatureGuardedRoute({
            featureNames: 'My Documents',
            minRole: 1,
            Component: RequiredDocuments
          })
        },
        {
          path: 'admin-company',
          element: makeFeatureGuardedRoute({
            featureNames: 'My Company',
            minRole: 1,
            Component: AdminCompany
          })
        },
        {path: 'feedbacks', element: withSuspense(Feedbacks)},
        {path: 'admin-activity-logs', element: withSuspense(AdminActionLogs)},
        {path: 'biometric', element: withSuspense(QrScanTimeKeep)},
        {path: 'holidays', element: withSuspense(Holidays)},
        {
          path: 'training-development',
          element: makeFeatureGuardedRoute({
            featureNames: 'Training And Development',
            minRole: 1,
            Component: AdminTrainingAndDevelopment
          })
        },
        {path: 'dice-game', element: withSuspense(AdminDiceGame)},
        {path: 'easter-game', element: withSuspense(AdminEasterGame)},
        {
          path: 'daily-questions',
          element: makeFeatureGuardedRoute({
            featureNames: 'Daily Questions',
            minRole: 3,
            Component: DailyQuestions
          })
        },
        {
          path: 'surveys',
          element: makeFeatureGuardedRoute({
            featureNames: ['Survey Management', 'Surveys'],
            minRole: 3,
            Component: Surveys
          })
        },
        {
          path: 'employee-request',
          element: makeFeatureGuardedRoute({
            featureNames: 'My Request',
            minRole: 1,
            Component: EmployeeRequest
          })
        },
        {path: 'version-control', element: withSuspense(VersionControl)},
        {
          path: 'passkey-policy',
          element: <Navigate to={[3, 4].includes(Number(userRole)) ? '/stores/features' : '/stores/app'} replace />,
        },
        {
          path: 'account-management/store-grouping',
          element: Number(userRole) === 4
            ? withSuspense(StoreGrouping)
            : <Navigate to="/stores/app" replace />,
        },
        {
          path: 'requests/:processFlowId',
          element: makeFeatureGuardedRoute({
            featureNames: 'My Request',
            minRole: 1,
            Component: StoreRequestsProcessPage
          })
        },
      ],
    },
    {
      path: 'sales',
      element: (
        <Suspense fallback={<StartingLoader />}>
          <MarketingLayout />
        </Suspense>
      ),
      children: [
        {index: true, element: withSuspense(MarketingIndexGate)},
        {path: 'features', element: withSuspense(MFeatures)},
        {path: 'features/:featureKey', element: withSuspense(MFeatureDetail)},
        {path: 'how-it-works', element: withSuspense(MHowItWorks)},
        {path: 'pricing', element: withSuspense(MPricing)},
        {path: 'about', element: withSuspense(MAbout)},
        {path: 'integrations', element: withSuspense(MIntegrations)},
        {path: 'updates', element: withSuspense(MUpdates)},
        {path: 'careers', element: withSuspense(MCareers)},
        {path: 'blog', element: withSuspense(MBlog)},
        {path: 'contact', element: withSuspense(MContact)},
        {path: 'help-center', element: withSuspense(MHelpCenter)},
        {path: 'docs', element: withSuspense(MDocs)},
        {path: 'api-reference', element: withSuspense(MApiReference)},
        {path: 'status', element: withSuspense(MStatus)},
      ],
    },
    {
      path: '/',
      children: [
        {
          element: withAuthSuspense(LoginLayout),
          children: [
            { index: true, element: withAuthSuspense(Login) },
            { path: 'signup', element: withAuthSuspense(RegistrationWizard) },
            { path: 'demo', element: withSuspense(DemoLogin) },
          ],
        },
        { path: 'login', element: <Navigate to="/" replace /> },
        { path: 'login/*', element: <Navigate to="/" replace /> },
        {path: 'store', element: withSuspense(StoreLogin)},
        {path: 'store/create', element: <StoreCreateRedirect />},
        {path: 'store/onboard', element: withSuspense(StoreOnboard)},
        {path: ':store/register', element: <EmployeeRegisterRedirect />},
        {path: ':store/onboard', element: withSuspense(Onboard)},
        {path: '404', element: withSuspense(NotFound)},
        {path: 'undefined', element: withSuspense(StoreMissing)},
        {path: 'track-time', element: withSuspense(TrackTime)},
        {path: 'support/console', element: withSuspense(SupportConsolePage)},
        {path: 'group/create', element: <GroupCreateRedirect />},
        {path: 'archived', element: withSuspense(ArchivedGate)},
      ],
    },
    {
      path: 'undefined',
      element: withSuspense(LogoOnlyLayout),
      children: [{path: 'login', element: <Navigate to="/" replace />}],
    },
    {path: '*', element: <Navigate to="/404" replace />},
  ])
}

export default AppRoute
